import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { AuditIssueListResult } from "@/modules/internal-audit/domain/repo/issue/list";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CheckCircleIcon,
  EditIcon,
  EyeIcon,
  MoreVertical,
  TrashIcon,
} from "lucide-react";

type AuditIssueRow = AuditIssueListResult["data"][number];

type AuditIssueTableProps = {
  data: AuditIssueRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClose?: (id: string) => void;
};

const severityLabels: Record<string, string> = {
  low: "ເບົາ",
  medium: "ກາງ",
  high: "ສູງ",
  critical: "ວິກິດ",
};

const severityColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  low: "secondary",
  medium: "default",
  high: "outline",
  critical: "destructive",
};

const statusLabels: Record<string, string> = {
  open: "ເປີດ",
  in_progress: "ກຳລັງແກ້ໄຂ",
  resolved: "ແກ້ໄຂແລ້ວ",
  closed: "ປິດ",
};

const statusColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  open: "destructive",
  in_progress: "outline",
  resolved: "default",
  closed: "secondary",
};

export function AuditIssueTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
  onClose,
}: AuditIssueTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<AuditIssueRow>[] = [
    createSortableColumn<AuditIssueRow>("title", "ຊື່ບັນຫາ", {
      size: 250,
    }),
    createSortableColumn<AuditIssueRow>("auditRecordName", "ພົບຈາກ", {
      size: 200,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.auditRecordName || "-"}
        </span>
      ),
    }),
    createSortableColumn<AuditIssueRow>("departmentName", "ພະແນກ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.departmentName || "-"}
        </span>
      ),
    }),
    createSortableColumn<AuditIssueRow>("severity", "ລະດັບຄວາມຮຸນແຮງ", {
      size: 150,
      cell: ({ row }) => (
        <Badge variant={severityColors[row.original.severity] || "default"}>
          {severityLabels[row.original.severity] || row.original.severity}
        </Badge>
      ),
    }),
    createSortableColumn<AuditIssueRow>("status", "ສະຖານະ", {
      size: 150,
      cell: ({ row }) => (
        <Badge variant={statusColors[row.original.status] || "default"}>
          {statusLabels[row.original.status] || row.original.status}
        </Badge>
      ),
    }),
    createSortableColumn<AuditIssueRow>("assignedToUserName", "ຜູ້ຮັບຜິດຊອບ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.assignedToUserName || "-"}
        </span>
      ),
    }),
    createSortableColumn<AuditIssueRow>("dueDate", "ວັນທີໝາຍ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.dueDate ? formatDate(row.original.dueDate) : "-"}
        </span>
      ),
    }),
    createSortableColumn<AuditIssueRow>("createdAt", "ວັນທີສ້າງ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    createSortableColumn<AuditIssueRow>("closedAt", "ວັນທີປິດ", {
      size: 150,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.closedAt ? formatDate(row.original.closedAt) : "-"}
        </span>
      ),
    }),
    {
      size: 80,
      id: "actions",
      cell: ({ row }: { row: { original: AuditIssueRow } }) => {
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive";
        }[] = [];

        if (has("internal_audit:read") && onView) {
          actions.push({
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id),
          });
        }

        if (
          has("internal_audit:update") &&
          onEdit &&
          row.original.status !== "closed"
        ) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original.id),
          });
        }

        if (
          has("internal_audit:update") &&
          onClose &&
          row.original.status !== "closed"
        ) {
          actions.push({
            label: "ປິດ",
            icon: <CheckCircleIcon className="h-4 w-4" />,
            onClick: () => onClose(row.original.id),
          });
        }

        if (has("internal_audit:delete") && onDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
          });
        }

        if (actions.length === 0) {
          return null;
        }

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {actions.map((action) => (
                <DropdownMenuItem
                  key={action.label}
                  onClick={action.onClick}
                  className={
                    action.variant === "destructive" ? "text-destructive" : ""
                  }
                >
                  {action.icon && <span className="mr-2">{action.icon}</span>}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable<AuditIssueRow, unknown>
      noDataMessage="ບໍ່ພົບບັນຫາການກວດສອບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0] || !sorting[0].id || sorting[0].id === "") return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
